import { createContext, useMemo, useState } from 'react';

import type { IReferenceContext } from 'src/components/contexts/ReferenceContext/ReferenceContext.types';
import type { IEnumReference } from 'src/types/reference/IEnumReference';
import type { IProductReference } from 'src/types/reference/IProductReference';
import type { IProductSpecificationReference } from 'src/types/reference/IProductSpecificationReference';

export const ReferenceContext = createContext<IReferenceContext>({
  isReferencesLoading: true,

  productTypes: [],
  productTypesRecord: {},

  productSpecifications: [],
  productSpecificationsRecord: {},

  enumReferences: [],
  enumReferencesRecord: {},

  // biome-ignore lint/suspicious/noEmptyBlockStatements: false
  setReferencesLoading: () => {},
  // biome-ignore lint/suspicious/noEmptyBlockStatements: false
  setProductTypes: () => {},
  // biome-ignore lint/suspicious/noEmptyBlockStatements: false
  setProductSpecifications: () => {},
  // biome-ignore lint/suspicious/noEmptyBlockStatements: false
  setEnumReferences: () => {},
});

export function useReferenceContextCreator() {
  const [isReferencesLoading, setIsReferencesLoading] = useState(true);

  const [productTypes, setProductTypes] = useState<IProductReference[]>([]);
  const [productTypesRecord, setProductTypesRecord] = useState<Record<string, IProductReference>>({});

  const [productSpecifications, setProductSpecifications] = useState<IProductSpecificationReference[]>([]);
  const [productSpecificationsRecord, setProductSpecificationsRecord] = useState<
    Record<string, IProductSpecificationReference>
  >({});

  const [enumReferences, setEnumReferences] = useState<IEnumReference[]>([]);
  const [enumReferencesRecord, setEnumReferencesRecord] = useState<Record<string, IEnumReference>>({});

  const referenceContext = useMemo<IReferenceContext>(
    () => ({
      isReferencesLoading,

      productTypes,
      productTypesRecord,

      productSpecifications,
      productSpecificationsRecord,

      enumReferences,
      enumReferencesRecord,

      setReferencesLoading: (newIsReferencesLoading: boolean) => setIsReferencesLoading(newIsReferencesLoading),

      setProductTypes: (newProductTypes: IProductReference[]) => {
        setProductTypes(newProductTypes);

        setProductTypesRecord(
          newProductTypes.reduce(
            (acc, curr) => {
              acc[curr.id] = curr;

              return acc;
            },
            {} as Record<string, IProductReference>
          )
        );
      },

      setProductSpecifications: (newProductSpecifications: IProductSpecificationReference[]) => {
        setProductSpecifications(newProductSpecifications);

        setProductSpecificationsRecord(
          newProductSpecifications.reduce(
            (acc, curr) => {
              acc[curr.id] = curr;

              return acc;
            },
            {} as Record<string, IProductSpecificationReference>
          )
        );
      },

      setEnumReferences: (newEnumReferences: IEnumReference[]) => {
        setEnumReferences(newEnumReferences);

        setEnumReferencesRecord(
          newEnumReferences.reduce(
            (acc, curr) => {
              acc[curr.id] = curr;

              return acc;
            },
            {} as Record<string, IEnumReference>
          )
        );
      },
    }),
    [
      isReferencesLoading,
      productTypes,
      productTypesRecord,
      productSpecifications,
      productSpecificationsRecord,
      enumReferences,
      enumReferencesRecord,
    ]
  );

  return referenceContext;
}
